/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Controller, FieldValues, SubmitHandler,
} from 'react-hook-form';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Container,
  DialogTitle,
  List,
  ListItem,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  useForm,
  getSubmitButtonProps,
  BackButton,
  useApiClient,
  getFieldProps,
  useFetch,
  TimezoneSelector,
} from '@marageti/z4-lib';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNavigate, useParams } from 'react-router-dom';
import { AvailabilityLimitType, Partner, PartnerAvailability } from '@marageti/z4-sdk/lib/travel';
import { TimeZone } from '@marageti/z4-sdk/lib/standards';
import { useSnackbar } from 'notistack';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import {
  add,
  addYears,
  isBefore,
  startOfDay,
} from 'date-fns';
import { useAuthStore } from '../../../store';
import {
  getEndOfDayInTimezone,
  getHelperText,
  getInitialDate,
  getStartOfDayInTimezone,
  getTargetTimezone,
  getTimezoneMismatchWarning,
  validateLeadCapacity,
} from '../utils';

const EditEvent = () => {
  const {
    control,
    setValue,
    getValues,
    formState: { errors, isSubmitting, isValid },
    handleSubmit,
    register,
    clearErrors,
    trigger,
  } = useForm();

  const { enqueueSnackbar } = useSnackbar();
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [submissionError, setSubmissionError] = useState<ApiError>();
  const {
    loggedInAgent,
  } = useAuthStore();

  const {
    data: partner,
  } = useFetch<Partner>(apiClient.partnersClient.getPartner, [loggedInAgent?.partnerId], !!loggedInAgent?.partnerId);

  const targetTimezone = useMemo(() => getTargetTimezone(loggedInAgent, partner), [loggedInAgent, partner]);
  const {
    data: event, isLoading, mutate: mutatePartnerAvailabilities,
  } = useFetch<PartnerAvailability>(apiClient.partnersClient.getPartnerAvailability, [loggedInAgent?.partnerId, eventId], true);

  const today = new Date();
  const maxDate = addYears(new Date(), 10);
  const [minEndDate, setMinEndDate] = useState(() => add(new Date(), { days: 1 }));

  const [selectedType, setSelectedType] = useState(event?.limitType);
  const [timezoneMismatchWarning, setTimezoneMismatchWarning] = useState<string | null>(null);

  useEffect(() => {
    if (event) {
      setSelectedType(event.limitType);
      setValue('timezone', { name: targetTimezone.name, rawOffsetInMinutes: targetTimezone.offsetMinutes });
      trigger();
      if (event.limitType === AvailabilityLimitType.LIMIT_TRAVEL && errors.weeklyCapacity) clearErrors('weeklyCapacity');

      const startDate = getInitialDate(event.startDate, targetTimezone.name);
      setMinEndDate(add(startDate, { days: 1 }));

      setTimezoneMismatchWarning(getTimezoneMismatchWarning(event.startDate, targetTimezone.name, targetTimezone.offsetMinutes));
    }
  }, [event, partner, targetTimezone]);

  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newType = e.target.value as AvailabilityLimitType;
    setSelectedType(newType);
    if (newType === AvailabilityLimitType.LIMIT_LEADS) {
      setValue('weeklyCapacity', 0);
    }
    if (newType === AvailabilityLimitType.LIMIT_TRAVEL) {
      setValue('weeklyCapacity', null);
      if (errors.weeklyCapacity) clearErrors('weeklyCapacity');
    }
  };

  const handleStartDateChange = (
    newDate: Date | null,
    onChange: (date: Date | null) => void,
  ) => {
    onChange(newDate);
    if (newDate) {
      const newMinEndDate = add(newDate, { days: 1 });
      setMinEndDate(newMinEndDate);
      const currentEndDate = getValues('endDate');
      if (currentEndDate && isBefore(currentEndDate, newMinEndDate)) {
        setValue('endDate', newMinEndDate);
      }
    }
  };

  const navigateBack = () => {
    navigate('/availability');
  };

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    if (event) {
      const timezoneOffsetInMinutes = data.timezone?.rawOffsetInMinutes ?? targetTimezone.offsetMinutes;

      const updatedPartnerAvaData: PartnerAvailability = {
        ...event,
        comments: data.comments,
        endDate: getEndOfDayInTimezone(data.endDate, timezoneOffsetInMinutes),
        leadsPerWeek: data.type === AvailabilityLimitType.LIMIT_TRAVEL ? null : data.weeklyCapacity,
        limitType: data.type,
        startDate: getStartOfDayInTimezone(data.startDate, timezoneOffsetInMinutes),
      };
      await apiClient.partnersClient.putPartnerAvailability(loggedInAgent?.partnerId!, eventId!, updatedPartnerAvaData)
        .then(() => {
          mutatePartnerAvailabilities();
          enqueueSnackbar('Event details saved.', { variant: 'success' });
          navigateBack();
        })
        .catch((e: ApiError) => {
          if (e.code === 401) navigate('/logout');
          enqueueSnackbar('Event updated failed.', { variant: 'default' });
          setSubmissionError(e);
        });
    }
  };

  return (
    <Container maxWidth="md" sx={{ pt: { mobile: 3, desktop: 6 }, mb: 20 }}>
      <BackButton aria-label="back to availability" onClick={() => navigateBack()} edge="start" sx={{ mb: 2 }} />
      <DialogTitle sx={{ p: 0, mb: 3 }}>Edit Capacity</DialogTitle>
      <form name="edit-availability-form" onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
        <Stack spacing={3}>
          {!isLoading ? (
            <Box>
              <Stack spacing={2}>
                <TextField
                  required
                  defaultValue={event?.limitType}
                  label="Type"
                  select
                  {...register('type')}
                  {...getFieldProps('type', errors)}
                  onChange={handleTypeChange}
                >
                  <MenuItem key={AvailabilityLimitType.LIMIT_LEADS} value={AvailabilityLimitType.LIMIT_LEADS}>Lead Capacity Restriction</MenuItem>
                  <MenuItem key={AvailabilityLimitType.LIMIT_TRAVEL} value={AvailabilityLimitType.LIMIT_TRAVEL}>Travel Date Restriction</MenuItem>
                </TextField>
                <List sx={{ mt: '0 !important', listStyleType: 'disc', pl: 3 }}>
                  <ListItem sx={{
                    p: 0, display: 'list-item', '&::marker': { color: 'primary.light' }, lineHeight: '1rem',
                  }}
                  >
                    <Typography variant="caption">
                      Use&nbsp;
                      <Typography variant="captionBold">Lead Capacity Restriction</Typography>
                  &nbsp;to restrict your lead flow on a specific period of time (e.g.: too busy, out of office, etc.)
                    </Typography>
                  </ListItem>
                  <ListItem sx={{
                    p: 0, display: 'list-item', '&::marker': { color: 'primary.light' }, lineHeight: '1rem',
                  }}
                  >
                    <Typography variant="caption">
                      Use&nbsp;
                      <Typography variant="captionBold">Travel Dates Restriction</Typography>
                  &nbsp;for travel dates you are unable to accommodate (e.g.: cherry blossom season is sold out)
                    </Typography>
                  </ListItem>
                </List>
                {selectedType === AvailabilityLimitType.LIMIT_LEADS && (
                <TextField
                  type="number"
                  defaultValue={event?.leadsPerWeek}
                  inputProps={{ min: 0, max: partner?.capacity && partner.capacity - 1 }}
                  label="Weekly Capacity"
                  {...register('weeklyCapacity', { valueAsNumber: true, validate: (value) => validateLeadCapacity(value, partner) })}
                  {...getFieldProps('weeklyCapacity', errors)}
                  helperText={getHelperText(errors, selectedType, partner)}
                />
                )}
                <Controller
                  name="startDate"
                  control={control}
                  defaultValue={getInitialDate(event?.startDate, targetTimezone.name)}
                  render={({
                    field: {
                      onChange, onBlur, value, ref, name,
                    },
                  }) => (
                    <DatePicker
                      label="Start Date"
                      onChange={(newDate) => handleStartDateChange(newDate, onChange)}
                      minDate={today}
                      maxDate={maxDate}
                      readOnly={!!(event?.startDate && isBefore(startOfDay(new Date(event.startDate)), startOfDay(today)))}
                      value={value}
                      views={['year', 'month', 'day']}
                      slotProps={{
                        textField: {
                          name,
                          onBlur,
                          error: !!errors.startDate,
                          sx: {
                            width: '100%',
                            '& .MuiInputBase-readOnly': {
                              backgroundColor: (event?.startDate && isBefore(startOfDay(new Date(event.startDate)), startOfDay(today))) ? '#f0f0f0' : 'transparent',
                              color: (event?.startDate && isBefore(startOfDay(new Date(event.startDate)), startOfDay(today))) ? 'rgba(0, 0, 0, 0.6)' : 'inherit',
                            },
                          },
                          helperText: errors.startDate?.message as string,
                        },
                      }}
                      inputRef={ref}
                    />
                  )}
                />
                <Controller
                  name="endDate"
                  control={control}
                  defaultValue={getInitialDate(event?.endDate, targetTimezone.name)}
                  render={({
                    field: {
                      onChange, onBlur, value, ref, name,
                    },
                  }) => (
                    <DatePicker
                      label="End Date"
                      onChange={onChange}
                      minDate={minEndDate}
                      maxDate={maxDate}
                      readOnly={!!(event?.endDate && isBefore(startOfDay(new Date(event.endDate)), startOfDay(today)))}
                      value={value}
                      views={['year', 'month', 'day']}
                      slotProps={{
                        textField: {
                          name,
                          onBlur,
                          error: !!errors.endDate,
                          sx: { width: '100%' },
                          helperText: errors.endDate?.message as string,
                        },
                      }}
                      inputRef={ref}
                    />
                  )}
                />
                <Controller
                  name="timezone"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TimezoneSelector
                      value={value}
                      onChange={(newValue: TimeZone | undefined) => {
                        onChange(newValue);
                        if (event?.startDate && newValue?.name) {
                          setTimezoneMismatchWarning(getTimezoneMismatchWarning(event.startDate, newValue.name, newValue.rawOffsetInMinutes));
                        }
                      }}
                      label="Timezone"
                    />
                  )}
                />
                {timezoneMismatchWarning && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <AlertTitle>Different Timezone</AlertTitle>
                    {timezoneMismatchWarning}
                  </Alert>
                )}
                <TextField
                  defaultValue={event?.comments}
                  label="Comments"
                  minRows={6}
                  maxRows={12}
                  multiline
                  required
                  sx={{
                    '.MuiOutlinedInput-root': {
                      padding: '10px 12px',
                      '> textarea': {
                        padding: '0 !important',
                      },
                    },
                  }}
                  {...register('comments', {
                    validate: (value: string) => value?.trim() !== '' || 'Comments are required',
                  })}
                  {...getFieldProps('comments', errors)}
                  error={Boolean(errors.comments)}
                  helperText={errors.comments ? errors.comments.message as string : ''}
                />
              </Stack>
            </Box>
          ) : <Skeleton variant="rounded" height={375} />}
          <Container
            sx={{
              display: 'flex',
              gap: '20px',
              flexDirection: {
                mobile: 'column-reverse',
                desktop: 'row',
              },
              px: '0 !important',
            }}
          >
            <LoadingButton
              type="submit"
              variant="contained"
              sx={{
                width: { desktop: 'fit-content' },
              }}
              {...getSubmitButtonProps(isSubmitting, isValid)}
            >
              Save
            </LoadingButton>
            <Button
              variant="outlined"
              onClick={() => navigateBack()}
              sx={{
                width: { desktop: 'fit-content' },
              }}
            >
              Cancel
            </Button>
          </Container>
          {submissionError && (
          <Alert severity="error">
            <AlertTitle>
              There was an error saving your event
            </AlertTitle>
            {submissionError.message}
            {submissionError.code && `Code: ${submissionError.code} `}
            {submissionError.id && `ID: ${submissionError.id}`}
          </Alert>
          )}
        </Stack>
      </form>
    </Container>
  );
};

export default EditEvent;
